import {
  type SexOffenderMatchDto,
  useSchoolDistrictParseDriverLicenseBarcode,
  useSchoolDistrictSearchSexOffenders,
} from "@crowdcoursing/api/openapi";
import { CameraAltOutlined, OpenInNewOutlined, WarningAmberOutlined } from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { normalizeRegistryUrl } from "./normalizeRegistryUrl";
import { VisitorIdScanner } from "./VisitorIdScanner";

const tTitle = "Visitor security check";
const tSubtitle = "Enter identity from the visitor's ID. Results are possible matches for front-desk review.";
const tFirstName = "First name";
const tLastName = "Last name";
const tZip = "ZIP code";
const tBirthDate = "Date of birth";
const tSearch = "Run registry check";
const tSearching = "Searching…";
const tClose = "Close";
const tNoMatches = "No registry matches found for this identity.";
const tMatchesFound = (count: number) => (count === 1 ? "1 possible match found" : `${count} possible matches found`);
const tNotAMatch = "Not a match — continue";
const tEscalate = "Escalate / deny entry";
const tAliases = "Aliases";
const tPhotoUnavailable = "No photo available";
const tScanId = "Scan ID with webcam";
const tReadingId = "Reading license…";
const tScanSuccess = "ID scanned successfully. Please review the fields before running the registry check.";
const tScanReadError = "We could not read this license. Please enter the visitor information manually.";

export interface VisitorSecurityCheckDialogProps {
  bubbleId: string;
  open: boolean;
  onClose: () => void;
  isKiosk: boolean;
}

function hasUsablePhotoUrl(photoUrl: string | null | undefined): photoUrl is string {
  return Boolean(photoUrl?.trim()) && photoUrl !== "N/A";
}

function MatchCard({ match }: { match: SexOffenderMatchDto }) {
  const nameFromParts = [match.lastName, match.firstName].filter(Boolean).join(", ");
  const displayName = match.fullName ?? (nameFromParts || "Unknown");
  const address = [match.addressLine1, match.addressLine2].filter(Boolean).join(" · ");

  return (
    <Card sx={{ borderColor: "warning.light" }} variant="outlined">
      <CardContent>
        <Stack alignItems="flex-start" direction={{ sm: "row", xs: "column" }} gap={2}>
          {hasUsablePhotoUrl(match.photoUrl) ? (
            <Avatar
              alt={displayName}
              src={normalizeRegistryUrl(match.photoUrl)}
              sx={{ border: 1, borderColor: "divider", height: 120, width: 96 }}
              variant="rounded"
            />
          ) : (
            <Box
              sx={{
                alignItems: "center",
                bgcolor: "action.hover",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                color: "text.secondary",
                display: "flex",
                height: 120,
                justifyContent: "center",
                px: 1,
                textAlign: "center",
                width: 96,
              }}
            >
              <Typography variant="caption">{tPhotoUnavailable}</Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {match.profileUrl ? (
              <Link
                href={normalizeRegistryUrl(match.profileUrl)}
                rel="noopener noreferrer"
                sx={{ alignItems: "center", display: "inline-flex", fontWeight: 600, gap: 0.25 }}
                target="_blank"
                variant="subtitle1"
              >
                {displayName}
                <OpenInNewOutlined fontSize="inherit" />
              </Link>
            ) : (
              <Typography fontWeight={600} variant="subtitle1">
                {displayName}
              </Typography>
            )}
            <Typography color="text.secondary" variant="body2">
              {[match.age != null ? `Age ${match.age}` : null, match.birthDate, match.sex, match.location, match.state]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
            {address ? (
              <Typography sx={{ mt: 0.5 }} variant="body2">
                {address}
              </Typography>
            ) : null}
            {match.aliases ? (
              <Typography color="text.secondary" sx={{ mt: 1 }} variant="caption">
                {tAliases}: {match.aliases}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function VisitorSecurityCheckDialog({ bubbleId, open, onClose, isKiosk }: VisitorSecurityCheckDialogProps) {
  const { data, error, isError, isPending, mutateAsync, reset } = useSchoolDistrictSearchSexOffenders();
  const {
    isPending: isParsingScan,
    mutateAsync: parseBarcode,
    reset: resetParse,
  } = useSchoolDistrictParseDriverLicenseBarcode();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanSuccess, setScanSuccess] = useState(false);

  const handleClose = () => {
    reset();
    resetParse();
    setFirstName("");
    setLastName("");
    setZipCode("");
    setBirthDate("");
    setIsScanning(false);
    setScanError(null);
    setScanSuccess(false);
    onClose();
  };

  const handleStartScan = () => {
    setScanError(null);
    setScanSuccess(false);
    setIsScanning(true);
  };

  const handleCancelScan = () => {
    setIsScanning(false);
  };

  const handleScanResult = async (barcodeText: string) => {
    setIsScanning(false);
    setScanError(null);
    setScanSuccess(false);

    try {
      const parsed = await parseBarcode({
        body: { barcodeText },
        pathParams: { bubbleId },
      });

      if (parsed.firstName) {
        setFirstName(parsed.firstName);
      }
      if (parsed.lastName) {
        setLastName(parsed.lastName);
      }
      if (parsed.dateOfBirth) {
        setBirthDate(parsed.dateOfBirth);
      }
      if (parsed.zipCode) {
        setZipCode(parsed.zipCode);
      }

      setScanSuccess(true);
    } catch {
      setScanError(tScanReadError);
    }
  };

  const canSearch = firstName.trim().length > 0 || lastName.trim().length > 0 || zipCode.trim().length > 0;

  const handleSearch = async () => {
    if (!canSearch) {
      return;
    }
    await mutateAsync({
      body: {
        birthDate: birthDate || undefined,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        zipCode: zipCode.trim() || undefined,
      },
      pathParams: { bubbleId },
    });
  };

  const results = data?.matches ?? [];
  const hasSearched = data !== undefined || isError;

  return (
    <Dialog fullScreen={isKiosk} fullWidth maxWidth="md" onClose={handleClose} open={open}>
      <DialogTitle>
        <Stack alignItems="center" direction="row" gap={1}>
          <WarningAmberOutlined color="warning" />
          <Box>
            <Typography component="span" variant={isKiosk ? "h5" : "h6"}>
              {tTitle}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {tSubtitle}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          {isScanning ? (
            <VisitorIdScanner onCancel={handleCancelScan} onScan={(text) => void handleScanResult(text)} />
          ) : (
            <>
              <Stack direction={{ sm: "row", xs: "column" }} gap={1} justifyContent="space-between">
                <Button
                  disabled={isParsingScan}
                  onClick={handleStartScan}
                  size={isKiosk ? "large" : "medium"}
                  startIcon={<CameraAltOutlined />}
                  variant="outlined"
                >
                  {tScanId}
                </Button>
                {isParsingScan ? (
                  <Stack alignItems="center" direction="row" gap={1}>
                    <CircularProgress size={16} />
                    <Typography color="text.secondary" variant="body2">
                      {tReadingId}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>

              {scanError ? <Alert severity="warning">{scanError}</Alert> : null}
              {scanSuccess ? <Alert severity="success">{tScanSuccess}</Alert> : null}

              <Stack direction={{ sm: "row", xs: "column" }} gap={2}>
                <TextField
                  fullWidth
                  label={tFirstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  size={isKiosk ? "medium" : "small"}
                  value={firstName}
                />
                <TextField
                  fullWidth
                  label={tLastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  size={isKiosk ? "medium" : "small"}
                  value={lastName}
                />
              </Stack>
              <Stack direction={{ sm: "row", xs: "column" }} gap={2}>
                <TextField
                  fullWidth
                  label={tZip}
                  onChange={(e) => setZipCode(e.target.value)}
                  size={isKiosk ? "medium" : "small"}
                  value={zipCode}
                />
                <TextField
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label={tBirthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  size={isKiosk ? "medium" : "small"}
                  type="date"
                  value={birthDate}
                />
              </Stack>

              <Button
                disabled={!canSearch || isPending}
                onClick={() => void handleSearch()}
                size={isKiosk ? "large" : "medium"}
                variant="contained"
              >
                {isPending ? (
                  <>
                    <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                    {tSearching}
                  </>
                ) : (
                  tSearch
                )}
              </Button>
            </>
          )}

          {!isScanning && isError ? (
            <Alert severity="error">
              {error instanceof Error ? error.message : "Registry check failed. Try again."}
            </Alert>
          ) : null}

          {!isScanning && hasSearched && !isPending && !isError ? (
            <>
              <Divider />
              {results.length === 0 ? (
                <Alert severity="success">{tNoMatches}</Alert>
              ) : (
                <>
                  <Alert severity="warning">{tMatchesFound(data?.matchCount ?? results.length)}</Alert>
                  <Stack gap={2}>
                    {results.map((match, index) => (
                      <MatchCard key={`${match.fullName ?? "match"}-${index}`} match={match} />
                    ))}
                  </Stack>
                </>
              )}
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, px: 3, py: 2 }}>
        {hasSearched && results.length > 0 ? (
          <>
            <Button onClick={handleClose} size={isKiosk ? "large" : "medium"} variant="outlined">
              {tNotAMatch}
            </Button>
            <Button color="error" onClick={handleClose} size={isKiosk ? "large" : "medium"} variant="contained">
              {tEscalate}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} size={isKiosk ? "large" : "medium"}>
            {tClose}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
